import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createJkBankAccount,
  deactivateJkBankAccount,
  fetchJkBankAccounts,
  lookupJkBankAccount,
  updateJkBankAccount,
} from "../../../services/jkBankAccountService";

export const fetchJkBankAccountsAsync = createAsyncThunk(
  "jkBankAccount/fetchAll",
  async (params, { rejectWithValue }) => {
    try {
      return await fetchJkBankAccounts(params);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch JK bank accounts"
      );
    }
  }
);

export const lookupJkBankAccountAsync = createAsyncThunk(
  "jkBankAccount/lookup",
  async (accountNumber, { rejectWithValue }) => {
    try {
      return await lookupJkBankAccount(accountNumber);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "JK bank account not found"
      );
    }
  }
);

export const createJkBankAccountAsync = createAsyncThunk(
  "jkBankAccount/create",
  async (payload, { rejectWithValue }) => {
    try {
      return await createJkBankAccount(payload);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data ||
          error.message ||
          "Failed to create JK bank account"
      );
    }
  }
);

export const updateJkBankAccountAsync = createAsyncThunk(
  "jkBankAccount/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await updateJkBankAccount(id, data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data ||
          "Failed to update JK bank account"
      );
    }
  }
);

// Flips IsActive on a JK bank account by reusing the full-update endpoint with
// the existing fields plus the new isActive value. Avoids needing a dedicated
// PATCH endpoint.
export const toggleJkBankAccountActiveAsync = createAsyncThunk(
  "jkBankAccount/toggleActive",
  async ({ row, nextActive }, { rejectWithValue }) => {
    try {
      const payload = {
        regionId: row.regionId,
        subRegionId: row.subRegionId ?? null,
        bankName: row.bankName,
        branchName: row.branchName ?? null,
        accountHolderName: row.accountHolderName,
        accountNumber: row.accountNumber,
        accountType: row.accountType ?? null,
        swiftCode: row.swiftCode ?? null,
        ibanCode: row.ibanCode ?? null,
        ifscCode: row.ifscCode ?? null,
        clabe: row.clabe ?? null,
        routingNumber: row.routingNumber ?? null,
        beneficiaryAddress: row.beneficiaryAddress ?? null,
        currency: row.currency,
        taxId: row.taxId ?? null,
        intermediaryBankName: row.intermediaryBankName ?? null,
        intermediaryBankAddress: row.intermediaryBankAddress ?? null,
        intermediarySwiftCode: row.intermediarySwiftCode ?? null,
        intermediaryAbaNumber: row.intermediaryAbaNumber ?? null,
        contractNumber: row.contractNumber ?? null,
        referenceNumber: row.referenceNumber ?? null,
        isActive: !!nextActive,
        // Deactivating a default account also clears the default flag so we don't
        // leave an inactive account marked default for its region.
        isDefaultForRegion: nextActive ? !!row.isDefaultForRegion : false,
      };
      return await updateJkBankAccount(row.id, payload);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data ||
          "Failed to toggle JK bank account status"
      );
    }
  }
);

export const deactivateJkBankAccountAsync = createAsyncThunk(
  "jkBankAccount/deactivate",
  async (id, { rejectWithValue }) => {
    try {
      await deactivateJkBankAccount(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const jkBankAccountSlice = createSlice({
  name: "jkBankAccount",
  initialState: {
    data: [],
    lookupResult: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearLookup: (state) => {
      state.lookupResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJkBankAccountsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJkBankAccountsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload || [];
      })
      .addCase(fetchJkBankAccountsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(lookupJkBankAccountAsync.fulfilled, (state, action) => {
        state.lookupResult = action.payload;
      })
      .addCase(lookupJkBankAccountAsync.rejected, (state) => {
        state.lookupResult = null;
      })

      .addCase(createJkBankAccountAsync.fulfilled, (state, action) => {
        if (action.payload) state.data.push(action.payload);
      })
      .addCase(createJkBankAccountAsync.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(updateJkBankAccountAsync.fulfilled, (state, action) => {
        const idx = state.data.findIndex((a) => a.id === action.payload?.id);
        if (idx !== -1) state.data[idx] = action.payload;
      })
      .addCase(updateJkBankAccountAsync.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(toggleJkBankAccountActiveAsync.fulfilled, (state, action) => {
        const idx = state.data.findIndex((a) => a.id === action.payload?.id);
        if (idx !== -1) state.data[idx] = action.payload;
      })
      .addCase(toggleJkBankAccountActiveAsync.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(deactivateJkBankAccountAsync.fulfilled, (state, action) => {
        const idx = state.data.findIndex((a) => a.id === action.payload);
        if (idx !== -1) state.data[idx].isActive = false;
      })
      .addCase(deactivateJkBankAccountAsync.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearLookup } = jkBankAccountSlice.actions;
export default jkBankAccountSlice.reducer;
